/**
 * mistralClient.ts — Wrapper für Mistral AI Chat Completions API.
 *
 * Hosting: api.mistral.ai (EU, Paris). Auth via Bearer-Token aus
 * VITE_MISTRAL_API_KEY (Cloudflare-Encrypted-Env-Var im Production-Deploy;
 * für lokale Tests in apps/web/.env.local — NIEMALS ins Git committen).
 *
 * Sicherheits-Garantien:
 *   1. Doppel-Gate: feature_flag 'llm_suggestions' UND VITE_MISTRAL_API_KEY
 *      müssen beide gesetzt sein. Sonst wird KEIN Call gemacht.
 *   2. PII-Pattern-Re-Check VOR dem API-Call. Selbst wenn die Pseudonymisierung
 *      einen Klartext durchgelassen hätte, blockt das hier.
 *   3. Jeder Call (auch geblockter / fehlgeschlagener) wandert in
 *      llm_usage_log + audit_log (action='LLM_CALL').
 *   4. Niemals den API-Key ins UI oder in Logs leaken.
 *
 * Diese Datei hat KEINE React-Abhängigkeit — sie ist als pure module nutzbar.
 */

import { getFeatureFlag } from '../supabaseFeatureFlags';
import { logLlmUsage } from '../supabaseLlmUsage';
import type { LlmCallStatus, LlmProvider } from '../supabaseTypes';

// --- Konfiguration --------------------------------------------------------

const MISTRAL_API_URL = 'https://api.mistral.ai/v1/chat/completions';
const DEFAULT_TEMPERATURE = 0.3;
const DEFAULT_MAX_TOKENS = 800;
const REQUEST_TIMEOUT_MS = 30_000;
const FEATURE_FLAG_KEY = 'llm_suggestions';

// --- PII-Pattern (Defense-in-depth) ---------------------------------------

/**
 * Letzte Verteidigungslinie: selbst wenn die Pseudonymisierung versagt hätte,
 * blockiert dieser Check den API-Call.
 *
 * Bewusst restriktiv:
 *   - PLZ + Stadtname (5 Ziffern + Wort, Großbuchstabe vorn)
 *   - E-Mail
 *   - IBAN (DE)
 *   - Internationale Telefonnummern (+49)
 *   - DE-Festnetz/Mobil (0...) mit Trennzeichen
 *
 * Wichtig: KEINE deutschen Vornamen-Listen — die haben zu viele false-
 * positives (z.B. wenn "Maja" in einer Branchenbezeichnung steht). Wir
 * verlassen uns auf die Pseudonymisierung in pseudonymize.ts für Namen.
 */
const PII_PATTERNS: ReadonlyArray<RegExp> = [
  /\b\d{5}\s+[A-ZÄÖÜ][a-zäöüß]+/u, // PLZ + Stadt
  /\b[\w.+-]+@[\w.-]+\.[A-Za-z]{2,}\b/, // E-Mail
  /\bDE\d{20}\b/, // IBAN
  /\+49[\s/()-]?\d[\d\s/()-]{6,}\d/, // Internationale DE-Nummer
  /\b0\d{2,4}[\s/-]\d[\d\s/-]{4,}\d\b/, // DE-Festnetz/Mobil
];

/**
 * Prüft auf PII-Pattern. Exportiert für Tests.
 * @returns true wenn mindestens ein Pattern matched
 */
export function containsPii(text: string): boolean {
  return PII_PATTERNS.some((p) => p.test(text));
}

// --- API-Key-Helper -------------------------------------------------------

/**
 * Liest den Mistral-API-Key aus VITE_MISTRAL_API_KEY (Build-Time-Injection).
 * Nicht direkt aufrufen — nur getApiKey() unten benutzt es.
 */
function readApiKey(): string | undefined {
  const env = (typeof import.meta !== 'undefined' ? import.meta.env : undefined) as
    | Record<string, string | undefined>
    | undefined;
  const k = env?.VITE_MISTRAL_API_KEY;
  if (typeof k !== 'string' || k.trim().length === 0) return undefined;
  return k.trim();
}

/**
 * @returns true wenn ENV-Var gesetzt ist. UI nutzt das, um den Toggle
 * deaktiviert/erklärt anzuzeigen ("API-Key fehlt im Deployment").
 */
export function isMistralConfigured(): boolean {
  return readApiKey() !== undefined;
}

// --- SHA-256-Hash ---------------------------------------------------------

/**
 * Berechnet hex-SHA-256-Hash eines Strings. Web Crypto, asynchron.
 * Nicht für Security — nur als unique-ID für den Logging-Zweck.
 *
 * Exportiert für Tests + Aufrufer, die selbst Hashes für Log-Einträge brauchen.
 */
export async function sha256Hex(input: string): Promise<string> {
  // Web Crypto in Browser + Node 22+
  const enc = new TextEncoder().encode(input);
  const buf = await crypto.subtle.digest('SHA-256', enc);
  return Array.from(new Uint8Array(buf))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

// --- Error-Codes ----------------------------------------------------------

/**
 * Bekannte Fehler-Codes, die Aufrufer programmatisch auswerten können.
 * Die UI mappt diese in deutsche Berater-Meldungen.
 */
export const MistralErrorCode = {
  MISTRAL_NOT_CONFIGURED: 'mistral_not_configured',
  LLM_FLAG_DISABLED: 'llm_flag_disabled',
  PII_DETECTED: 'pii_detected',
  HTTP_ERROR: 'http_error',
  TIMEOUT: 'timeout',
  PARSE_ERROR: 'parse_error',
} as const;

export type MistralErrorCodeValue = (typeof MistralErrorCode)[keyof typeof MistralErrorCode];

export class MistralError extends Error {
  public readonly code: MistralErrorCodeValue;
  public readonly httpStatus: number | undefined;

  constructor(code: MistralErrorCodeValue, message: string, httpStatus?: number) {
    super(message);
    this.code = code;
    this.httpStatus = httpStatus;
    this.name = 'MistralError';
  }
}

// --- Public API -----------------------------------------------------------

export interface MistralOptions {
  readonly model: LlmProvider;
  readonly temperature?: number;
  readonly maxTokens?: number;
  /**
   * Optional — wenn gesetzt, wird der Eintrag in llm_usage_log mit dieser
   * caseId verknüpft. Sonst case_id = NULL (z.B. für Smoke-Tests im Admin-
   * Cockpit ohne aktiven Case).
   */
  readonly caseId?: string | null;
  /**
   * Welches AssessmentTexts-Feld bedient wird — wandert ins Log.
   */
  readonly fieldKey: string;
  /**
   * Test-Hook: erlaubt das Überschreiben des fetch-Implementations für Tests.
   * Production-Code lässt das weg → globales fetch.
   */
  readonly fetchImpl?: typeof fetch;
}

export interface MistralResponse<T = string> {
  readonly content: T;
  readonly promptTokens: number;
  readonly completionTokens: number;
  readonly totalTokens: number;
  readonly durationMs: number;
}

/**
 * Kern-Helper: alle Sicherheits-Checks vor jedem API-Call. Liefert
 *   { apiKey } bei OK
 *   throws MistralError und loggt vorher in llm_usage_log
 */
async function preflightOrThrow(opts: {
  caseId: string | null;
  fieldKey: string;
  provider: LlmProvider;
  textToSend: string;
}): Promise<{ apiKey: string; hash: string }> {
  // 1. Flag-Check
  let flagEnabled = false;
  try {
    flagEnabled = await getFeatureFlag(FEATURE_FLAG_KEY);
  } catch (e) {
    // Wenn DB nicht erreichbar ist: nicht aufrufen. Fail-closed.
    const msg = e instanceof Error ? e.message : 'unknown';
    throw new MistralError(MistralErrorCode.LLM_FLAG_DISABLED, `Flag-Check fehlgeschlagen: ${msg}`);
  }
  if (!flagEnabled) {
    const hash = await sha256Hex(opts.textToSend);
    await safeLog({
      caseId: opts.caseId,
      fieldKey: opts.fieldKey,
      provider: opts.provider,
      pseudonymizedHash: hash,
      status: 'blocked_flag',
      errorMessage: 'feature_flag llm_suggestions ist deaktiviert',
    });
    throw new MistralError(
      MistralErrorCode.LLM_FLAG_DISABLED,
      'LLM-Vorschläge sind im System deaktiviert (feature_flag llm_suggestions = false).',
    );
  }

  // 2. API-Key-Check
  const apiKey = readApiKey();
  if (!apiKey) {
    throw new MistralError(
      MistralErrorCode.MISTRAL_NOT_CONFIGURED,
      'VITE_MISTRAL_API_KEY ist nicht gesetzt — Mistral-Integration nicht verfügbar.',
    );
  }

  // 3. PII-Pattern-Re-Check (Defense-in-depth)
  if (containsPii(opts.textToSend)) {
    const hash = await sha256Hex(opts.textToSend);
    await safeLog({
      caseId: opts.caseId,
      fieldKey: opts.fieldKey,
      provider: opts.provider,
      pseudonymizedHash: hash,
      status: 'blocked_pseudonymization',
      errorMessage: 'PII-Pattern im pseudonymisierten Input erkannt',
    });
    throw new MistralError(
      MistralErrorCode.PII_DETECTED,
      'Input enthält PII-Pattern trotz Pseudonymisierung — Call abgebrochen. Bug an Entwickler melden.',
    );
  }

  const hash = await sha256Hex(opts.textToSend);
  return { apiKey, hash };
}

async function safeLog(args: {
  caseId: string | null;
  fieldKey: string;
  provider: LlmProvider;
  pseudonymizedHash: string;
  status: LlmCallStatus;
  promptTokens?: number;
  completionTokens?: number;
  totalTokens?: number;
  durationMs?: number;
  errorMessage?: string;
}): Promise<void> {
  try {
    await logLlmUsage(args);
  } catch (e) {
    console.error('mistralClient.safeLog: logLlmUsage warf', e);
  }
}

/**
 * Klassischer Chat-Completion-Call. Liefert reinen Text zurück.
 *
 * @throws MistralError mit programmatisch auswertbarem `code`-Feld.
 */
export async function callMistral(
  systemPrompt: string,
  userPrompt: string,
  opts: MistralOptions,
): Promise<MistralResponse<string>> {
  const caseId = opts.caseId ?? null;
  const provider = opts.model;
  const textToSend = systemPrompt + '\n' + userPrompt;

  const { apiKey, hash } = await preflightOrThrow({
    caseId,
    fieldKey: opts.fieldKey,
    provider,
    textToSend,
  });

  const fetchImpl = opts.fetchImpl ?? fetch;
  const start = Date.now();
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);

  try {
    const resp = await fetchImpl(MISTRAL_API_URL, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${apiKey}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        model: opts.model,
        temperature: opts.temperature ?? DEFAULT_TEMPERATURE,
        max_tokens: opts.maxTokens ?? DEFAULT_MAX_TOKENS,
        messages: [
          { role: 'system', content: systemPrompt },
          { role: 'user', content: userPrompt },
        ],
      }),
      signal: controller.signal,
    });
    clearTimeout(timeoutId);

    const duration = Date.now() - start;
    if (!resp.ok) {
      const body = await resp.text().catch(() => '');
      const errMsg = `HTTP ${String(resp.status)} ${resp.statusText} — ${body.slice(0, 200)}`;
      await safeLog({
        caseId,
        fieldKey: opts.fieldKey,
        provider,
        pseudonymizedHash: hash,
        status: 'error',
        durationMs: duration,
        errorMessage: errMsg,
      });
      throw new MistralError(MistralErrorCode.HTTP_ERROR, errMsg, resp.status);
    }

    const json = (await resp.json()) as MistralRawResponse;
    const content = json.choices?.[0]?.message?.content;
    if (typeof content !== 'string' || content.length === 0) {
      await safeLog({
        caseId,
        fieldKey: opts.fieldKey,
        provider,
        pseudonymizedHash: hash,
        status: 'error',
        durationMs: duration,
        errorMessage: 'Mistral-Response ohne content',
      });
      throw new MistralError(MistralErrorCode.PARSE_ERROR, 'Mistral-Response ohne content');
    }

    const usage = json.usage ?? { prompt_tokens: 0, completion_tokens: 0, total_tokens: 0 };
    await safeLog({
      caseId,
      fieldKey: opts.fieldKey,
      provider,
      pseudonymizedHash: hash,
      status: 'success',
      promptTokens: usage.prompt_tokens,
      completionTokens: usage.completion_tokens,
      totalTokens: usage.total_tokens,
      durationMs: duration,
    });

    return {
      content,
      promptTokens: usage.prompt_tokens,
      completionTokens: usage.completion_tokens,
      totalTokens: usage.total_tokens,
      durationMs: duration,
    };
  } catch (e) {
    clearTimeout(timeoutId);
    if (e instanceof MistralError) throw e;
    const isAbort = e instanceof Error && e.name === 'AbortError';
    const code = isAbort ? MistralErrorCode.TIMEOUT : MistralErrorCode.HTTP_ERROR;
    const msg = e instanceof Error ? e.message : 'unknown fetch error';
    await safeLog({
      caseId,
      fieldKey: opts.fieldKey,
      provider,
      pseudonymizedHash: hash,
      status: 'error',
      durationMs: Date.now() - start,
      errorMessage: `${code}: ${msg}`,
    });
    throw new MistralError(code, msg);
  }
}

/**
 * Strukturierter Call mit JSON-Schema-Constraint. Mistral unterstützt
 * `response_format: { type: 'json_object' }`. Schema-Validation passiert
 * client-seitig nach dem Call (mit Zod im Aufrufer).
 */
export async function callMistralJson<T>(
  systemPrompt: string,
  userPrompt: string,
  jsonSchema: Record<string, unknown>,
  opts: MistralOptions,
): Promise<MistralResponse<T>> {
  // Wir hängen das Schema in den User-Prompt — Mistral nimmt response_format:
  // 'json_object' und folgt dem Schema-Hint im Text. Für striktes Schema-
  // Forcing müssten wir auf function calling umsteigen; das ist ein
  // expliziter Sprint-4-Punkt.
  const schemaHint = `\n\nAntworte als JSON gemäß diesem Schema:\n${JSON.stringify(jsonSchema, null, 2)}`;
  const fullUserPrompt = userPrompt + schemaHint;

  const raw = await callMistral(systemPrompt, fullUserPrompt, opts);
  let parsed: T;
  try {
    parsed = JSON.parse(raw.content) as T;
  } catch (e) {
    const msg = e instanceof Error ? e.message : 'JSON.parse failed';
    throw new MistralError(MistralErrorCode.PARSE_ERROR, `Mistral-JSON-Parse-Fehler: ${msg}`);
  }
  return {
    content: parsed,
    promptTokens: raw.promptTokens,
    completionTokens: raw.completionTokens,
    totalTokens: raw.totalTokens,
    durationMs: raw.durationMs,
  };
}

// --- Internal types -------------------------------------------------------

interface MistralRawResponse {
  choices?: ReadonlyArray<{ message?: { content?: string } }>;
  usage?: {
    prompt_tokens: number;
    completion_tokens: number;
    total_tokens: number;
  };
}
